// Coordinator ported from data_centrifuge_view.coffee. Owns all coordinator state,
// the AJAX job-polling state machine, restore/reject, and the vector-toggle handlers
// — a 1:1 port of the Backbone class so behavior stays identical. Pure data transforms
// live in the sibling builder modules (calc-data, list-props-builder,
// component-props-builder, vector-predicates).
//
// React is a pure subscriber: state lives here in a plain object (created before React
// mounts, so the imperative methods page_router/page_view call are always available).
// After any state change, render() rebuilds listProps, repaints the sidebar, and
// notify()s React to re-render the Chart + Responses. This keeps the legacy
// "set listProps -> renderDataCentrifugeProps(true)" sequence atomic.

import {
  VECTORS_RAW,
  VECTORS_GROUPS,
  VECTORS_NAMES,
  DEFAULT_FILTER,
  DEFAULT_SUSPICION_LEVEL,
  getVisibleRaw,
  getDefaultDisabledVectors
} from '@aytm/app/stats/page/data-centrifuge/lib/vectors'
import { FULFILL_QUARK, ANIMATION_DELAY } from '@aytm/app/stats/page/data-centrifuge/lib/utils'
import { SECTION } from '@aytm/app/stats/page/data-quality-report/lib/sections'

import { calcData, seedEmptyGroups } from '@aytm/app/stats/page/data-centrifuge/lib/calc-data'
import { calcListProps } from '@aytm/app/stats/page/data-quality-report/controller/list-props-builder'
import {
  isAutoEnabledLikeVector,
  isAutoEnabledLikeSubVector
} from '@aytm/app/stats/page/data-quality-report/controller/vector-predicates'
import {
  fetchDataQualityReport,
  fetchDataCentrifugeJob,
  restoreResponses,
  rejectResponse
} from '@aytm/app/stats/page/data-quality-report/controller/api-calls'
import { mapDqrReport } from '@aytm/app/stats/page/data-quality-report/controller/dqr-response'
import { canReviewEvidence, tierFromFeatures } from '@aytm/app/stats/page/data-quality-report/lib/tiers'

// Treat an aborted fetch (mode switch / re-poll cancel) as a no-op, mirroring the
// coffee error guard `return if !jqXHR.getAllResponseHeaders() && 'error' != statusText`.
const isAbort = error => error?.name === 'AbortError'

const DEFAULT_DELAY = ANIMATION_DELAY

// Empty data skeleton — serves both the pre-fetch `this.data` placeholder (calcData
// early-returns when there's no `cs`) and the non-terminal-status `componentData`
// (the `else` branch of setData). Mirrors the shape calcData produces.
const emptyComponentData = () => ({
  vectorIds: [],
  vectorNames: [],
  scores: [],
  groups: {},
  answerScores: {},
  suspicionScores: {},
  excludedScores: [],
  responses: {},
  scoresUIDs: [],
  restoringUIDs: [],
  quarantineUIDs: [],
  dcStatuses: {},
  disabledVectorsScores: {},
  advisoryUIDs: [],
  advisoryScores: {}
})

// O(1) single-pass dedup preserving first-seen order (replaces the coffee's hash dedup).
const dedup = arr => {
  const seen = {}
  return arr.filter(x => (seen[x] ? false : (seen[x] = true)))
}

// Seed every visible raw vector to the same status (waiting / error).
const vectorStatusMap = status =>
  getVisibleRaw().reduce((acc, id) => {
    acc[id] = status
    return acc
  }, {})

export default class Controller {
  // deps injects the Backbone apiKey surface (sourced by the shim, exactly like
  // specials_view.coffee feeds apiKey/onFetchApiKey to the React specials). The React
  // controller never touches page.model.get('apiKey') / page.bridge.* directly.
  //   deps = { getApiKey(), isApiKeyInvalid(), fetchApiKey({forceUpdate, callback}),
  //            delayFetchApiKey({forceUpdate, status, callback}) }
  constructor(shim, deps) {
    this.shim = shim
    this.deps = deps
    this.subscribers = new Set()
    this.destroyed = false
    this.viewActive = false

    this.delay = DEFAULT_DELAY
    this.jobData = { status: 'disabled' }

    this.key = null
    this.data = emptyComponentData()
    this.cachedData = null
    this.componentData = null
    this._calcDataCache = null
    this.report = null
    this.reportLoadedAt = null

    this.disabledVectors = []
    // The reader's own vector selection, kept for the life of the tab the way severity and filter
    // already are. `null` means the checklist is untouched, which is what still lets the first-load
    // default seed apply.
    this.vectorSelection = null
    this.suspicionLevel = DEFAULT_SUSPICION_LEVEL
    this.filter = DEFAULT_FILTER

    this.responsesAjax = {}
    this.includeVectors = null

    // Whether the DQR's Data Centrifuge accordion section is open. Published onto listProps so
    // the Quality sidebar panel can gate the DC controls. Set via setSectionOpen().
    this.sectionOpen = false

    this.abortController = null
    this.ajaxTimer = null

    // Bind the handlers handed to React / external Backbone callers.
    this.onCheck = this.onCheck.bind(this)
    this.onRangeCheck = this.onRangeCheck.bind(this)
    this.onSelect = this.onSelect.bind(this)
    this.onRestore = this.onRestore.bind(this)
    this.onReject = this.onReject.bind(this)
    this.onFilter = this.onFilter.bind(this)
    this.delayFetchJob = this.delayFetchJob.bind(this)

    // Seed the listProps skeleton (callbacks + empty collections) so sidebar_view's
    // `Object.assign({}, page.dataCentrifugeView.listProps)` never reads undefined before
    // the first commit. calcListProps mutates this object in place, preserving the
    // callbacks. The shim exposes it via a getter — this controller is the sole owner.
    this.listProps = {
      tier: tierFromFeatures(this.deps.getFeatures()),
      vectors: [],
      statuses: {},
      scoresUIDs: [],
      restoringUIDs: [],
      quarantineUIDs: [],
      quarantineUIDsFiltered: [],
      // `null` means no figure has been received, which is not the same claim as zero — the
      // sidebar's Export affordance falls back to the uid set on the first and hides on the second.
      quarantineCount: null,
      surveyStatus: null,
      sectionOpen: false,
      onCheck: this.onCheck,
      onRangeCheck: this.onRangeCheck,
      onSelect: this.onSelect,
      onRestore: this.onRestore,
      onFilter: this.onFilter,
      onFetch: this.delayFetchJob
    }
  }

  // Thin pass-throughs to injected Backbone deps, consumed by Controller.jsx so the
  // React view never touches page.* directly.
  getFeatures() {
    return this.deps.getFeatures()
  }

  getWidth() {
    return this.deps.getWidth()
  }

  onFilter(pattern, filter) {
    this.deps.applyUserFilter(pattern, filter)
  }

  openTermReport() {
    this.deps.openTermReport()
  }

  scrollToQuestion(id, batch) {
    this.deps.scrollToQuestion(id, batch)
  }

  // ---- React subscription -------------------------------------------------

  subscribe(cb) {
    this.subscribers.add(cb)
    return () => this.subscribers.delete(cb)
  }

  notify() {
    if (this.destroyed) return
    this.subscribers.forEach(cb => cb())
  }

  // Wire-first tier (feature fallback), so every consumer gates on the same value the sidebar does.
  resolveTier() {
    return this.report?.tier || tierFromFeatures(this.deps.getFeatures())
  }

  // Resolved once here so every evidence surface gates on the same answer, the way they already
  // share one tier. Before the report lands the tier decides alone, which is what the grant's
  // absent-reads-as-granted rule already gives.
  resolveReviewEvidence() {
    return canReviewEvidence(this.resolveTier(), this.report?.sections?.[SECTION.DATA_CENTRIFUGE]?.reviewEvidence)
  }

  // State snapshot consumed by the list-props / component-props builders.
  snapshot() {
    return {
      tier: this.resolveTier(),
      data: this.data,
      jobData: this.jobData,
      componentData: this.componentData,
      disabledVectors: this.disabledVectors,
      filter: this.filter,
      suspicionLevel: this.suspicionLevel,
      features: this.deps.getFeatures(),
      surveyConfig: this.deps.getSurveyConfig()
    }
  }

  // ---- Render seam --------------------------------------------------------

  // Port of renderDataCentrifugePanel's sidebar half: build listProps in place, repaint.
  renderPanel() {
    if (!this.componentData) return
    this.listProps = calcListProps(this.listProps, this.snapshot())
    this.listProps.tier = this.resolveTier()
    // The aggregate quarantine count rides at every tier, while the per-respondent uid set is
    // withheld below the forensic tiers — so the sidebar's Export affordance counts this instead.
    // Absence is preserved rather than collapsed to zero: a payload that carries no figure and one
    // that reports none are different answers, and the sidebar acts differently on each.
    this.listProps.quarantineCount = this.report?.sections?.[SECTION.DATA_CENTRIFUGE]?.quarantineCount ?? null
    // The Data Centrifuge runs while the survey fields, so the sidebar's summary needs the survey's
    // own lifecycle to say whether its account is final — the same marker the report's subtitle reads.
    this.listProps.surveyStatus = this.report?.surveyStatus ?? null
    // Re-apply the DQR section-open flag (calcListProps may not preserve it).
    this.listProps.sectionOpen = this.sectionOpen
    this.deps.renderSidebar()
  }

  // The Data Centrifuge section subtitle summarises the body below it, whose quarantined figure the
  // engine recomputes on a vector toggle. Null below the forensic tiers, where the per-respondent
  // rows are withheld and there is nothing live to derive from — the caller keeps the committed
  // aggregate, which cannot diverge there anyway since those tiers are read-only.
  getLiveQuarantineCount() {
    if (!this.data?.cs?.length) return null

    return this.listProps.quarantineUIDsFiltered?.length ?? null
  }

  // Publish the DQR Data Centrifuge accordion's open state to the Quality sidebar panel
  // (model.dataCentrifuge.sectionOpen). Repaints the sidebar immediately.
  setSectionOpen(open) {
    this.sectionOpen = open
    this.listProps.sectionOpen = open
    this.deps.renderSidebar()
  }

  // Replaces the coffee trio renderDataCentrifugePanel + renderComponent + renderResponses.
  render() {
    this.viewActive = true
    this.renderPanel()
    this.notify()
  }

  // Replaces destroyReactComponents (+ destroyComponent's disabledVectors reset).
  destroyViews() {
    this.viewActive = false
    this.disabledVectors = this.rememberedDisabledVectors()
    this.notify()
  }

  // Called once by the shim after construction (mirrors initialize's setData + panel render).
  init() {
    this.setData()
    this.renderPanel()
  }

  // Cross-feature dispatch from the Chart's onChange/onCallback (port of the
  // renderComponentModel `callback`, data_centrifuge_view.coffee:573-590).
  handleCallback(data) {
    if (Object.hasOwn(data, 'progress')) {
      if (!this.jobData.progress) return
      if (data.progress.current !== undefined) this.jobData.progress.current = data.progress.current
    } else if (Object.hasOwn(data, 'action')) {
      if (data.action === 'restore') this.onRestore(data.uids, data.callback)
      if (data.action === 'reject') this.onReject(data.uid, data.reason, data.callback)
    } else {
      // Generic assignment (e.g. filter, suspicionLevel) then re-render — no recompute.
      Object.keys(data).forEach(key => {
        this[key] = data[key]
      })
      this.render()
    }
  }

  // ---- Vector toggles -----------------------------------------------------

  // Records what the reader chose, so the fetch / run lifecycle restores it rather than seeding
  // the defaults over the top.
  commitVectorSelection() {
    this.disabledVectors = dedup(this.disabledVectors)
    this.vectorSelection = [...this.disabledVectors]
  }

  // What the checklist reverts to when a lifecycle step clears it. An untouched checklist reverts
  // to empty and falls through to the first-load default seed in setData().
  rememberedDisabledVectors() {
    return this.vectorSelection ? [...this.vectorSelection] : []
  }

  // A parent's checkbox mirrors its children: off once every live child is off, on again as soon as
  // one live child is on. Walks the static raw list so auto-enabled-like vectors participate in
  // scope. Re-derived rather than remembered, because a run changes which children are live under a
  // parent and a remembered parent id would then contradict the rows below it.
  reconcileGroupParents() {
    const liveIds = new Set(liveVectorIds(this.componentData))

    Object.keys(this.componentData.groups).forEach(id => {
      if (!liveIds.has(id)) return

      const allChildrenOff = Object.keys(this.componentData.groups[id]).every(
        _id =>
          this.disabledVectors.includes(_id) ||
          (!isAutoEnabledLikeSubVector(this.componentData, _id, this.componentData.groups[id][_id]) &&
            !this.componentData.groups[id][_id].total)
      )

      this.disabledVectors = allChildrenOff
        ? dedup(this.disabledVectors.concat([id])) // the restored selection may already carry it
        : this.disabledVectors.filter(_id => _id !== id)
    })
  }

  onCheck(id, gid, checked) {
    const groups = this.componentData.groups
    if (checked) {
      if (Object.hasOwn(groups, id) && Object.hasOwn(groups[id], gid)) {
        this.disabledVectors = this.disabledVectors.filter(_id => _id !== id && _id !== gid)
      } else if (Object.hasOwn(groups, id)) {
        const childIds = new Set(Object.keys(groups[id]))
        this.disabledVectors = this.disabledVectors.filter(_id => _id !== id && !childIds.has(_id))
      } else {
        this.disabledVectors = this.disabledVectors.filter(_id => _id !== id)
      }
    } else if (Object.hasOwn(groups, id) && Object.hasOwn(groups[id], gid)) {
      this.disabledVectors = this.disabledVectors.concat([gid])
      if (
        Object.keys(groups[id]).every(
          _id =>
            this.disabledVectors.includes(_id) ||
            (!isAutoEnabledLikeSubVector(this.componentData, _id, groups[id][_id]) && !groups[id][_id].total)
        )
      ) {
        this.disabledVectors = this.disabledVectors.concat([id])
      }
    } else {
      this.disabledVectors = this.disabledVectors.concat([id])
      if (Object.hasOwn(groups, id)) {
        this.disabledVectors = this.disabledVectors.concat(
          Object.keys(groups[id]).filter(
            _id => isAutoEnabledLikeSubVector(this.componentData, _id, groups[id][_id]) || groups[id][_id].total > 0
          )
        )
      }
    }

    this.commitVectorSelection()
    this.setData()
    this.render()
  }

  onRangeCheck(ids, checked) {
    if (checked) {
      this.disabledVectors = this.disabledVectors.filter(_id => !ids.includes(_id))
    } else {
      this.disabledVectors = this.disabledVectors.concat(ids)
    }

    this.reconcileGroupParents()
    this.commitVectorSelection()
    this.setData()
    this.render()
  }

  onSelect(id, gid) {
    const vectorIds = liveVectorIds(this.componentData)

    // Flat list of every "live" child id across all groups.
    // NOTE: mirrors the legacy coffee verbatim — group-child items carry no `id`
    // (they are { text, total, scores }), so `item.id` is undefined and the
    // auto-enabled-like check is a latent no-op; inclusion effectively reduces to
    // total > 0. Preserved as-is to avoid changing the all/solo toggle scope.
    const allLiveChildIds = []
    Object.entries(this.componentData.groups).forEach(([, children]) => {
      Object.entries(children).forEach(([childId, childItem]) => {
        if (isAutoEnabledLikeSubVector(this.componentData, childId, childItem) || 0 < childItem.total)
          allLiveChildIds.push(childId)
      })
    })

    if (id === 'all') {
      if (0 < this.disabledVectors.length) {
        this.disabledVectors = []
      } else {
        this.disabledVectors = vectorIds.concat(allLiveChildIds)
      }
    } else {
      this.disabledVectors = vectorIds.filter(_id => _id !== id)
      const groupChildren = this.componentData.groups[id]
      this.disabledVectors = this.disabledVectors.concat(allLiveChildIds.filter(_id => !groupChildren?.[_id]))
      if (Object.hasOwn(this.componentData.groups, id) && Object.hasOwn(this.componentData.groups[id], gid)) {
        const subVectors = Object.keys(this.componentData.groups[id]).filter(
          _id =>
            _id !== gid &&
            (isAutoEnabledLikeSubVector(this.componentData, _id, this.componentData.groups[id][_id]) ||
              0 < this.componentData.groups[id][_id].total)
        )
        this.disabledVectors = this.disabledVectors.concat(subVectors)
      }
    }

    this.commitVectorSelection()
    this.setData()
    this.render()
  }

  // ---- Restore / reject ---------------------------------------------------

  onRestore(uids, callback) {
    const isArray = Array.isArray(uids)
    const key = (isArray && 'all') || uids
    // Drop array uids already in flight (scalar passes through unchanged).
    const targetUids = isArray ? uids.filter(uid => !Object.hasOwn(this.responsesAjax, uid)) : uids

    const controller = new AbortController()
    this.responsesAjax[key] ||= {}
    this.responsesAjax[key].status = 'restoring'
    this.responsesAjax[key].callback = callback
    this.responsesAjax[key].controller = controller
    if (isArray) this.responsesAjax[key].uids = targetUids

    restoreResponses({ surveyId: this.deps.getSurveyId(), uids: targetUids, signal: controller.signal })
      .then(response => this.onRestoreCallback(targetUids, response))
      .catch(error => {
        if (!isAbort(error)) this.onRestoreCallback(targetUids, { status: 'FAILED' })
      })

    // In-place status mutation on matching cs items.
    this.data.cs.forEach(item => {
      if ((isArray && targetUids.includes(item.response.uid)) || (!isArray && targetUids === item.response.uid)) {
        item.status = 'dc-restoring'
      }
    })

    this.invalidateCalcCache()
    this.setData()
    this.render()
  }

  onRestoreCallback(uids, data) {
    const isArray = Array.isArray(uids)
    const key = (isArray && 'all') || uids

    if (this.deps.getMode() === 'data-quality') {
      // Orphan callback: the request outlived a mode tear-down (resetData drops the
      // callback but leaves the request running) and resolved after re-entry, before the
      // fresh fetch repopulated the set. this.data is the empty skeleton (no `cs`), so the
      // branch below would throw on `this.data.cs` — treat the orphan as a no-op.
      if (!this.data.cs) {
        delete this.responsesAjax[key]
        return
      }

      let error = null

      const matches = uid => (isArray && uids.includes(uid)) || (!isArray && uids === uid)

      if (data.status === 'OK') {
        this.deps.onFetch(null, false)

        this.data.cs.forEach(item => {
          if (data.updated_ids.includes(item.response.uid)) {
            item.status = 'completed'
            item.restored = true
          } else if (matches(item.response.uid)) {
            item.status = 'dc-auto-rejected'
          }
        })
        if ((isArray && uids.length !== data.updated_ids.length) || (!isArray && !data.updated_ids.includes(uids))) {
          const amount = (isArray && uids.length - data.updated_ids.length) || 1
          error = { message: data.error || `${amount} response${amount === 1 ? " wasn't" : "s weren't"} released` }
        }
      } else {
        this.data.cs.forEach(item => {
          if (matches(item.response.uid)) item.status = 'dc-auto-rejected'
        })
        error = { message: data.error || 'Oops. Something went wrong.' }
      }

      this.invalidateCalcCache()
      this.setData()
      this.render()

      const score = this.findContributingScore(matches)
      if (isArray || score) this.responsesAjax[key]?.callback?.(error)
    } else {
      this.resetData()
    }

    delete this.responsesAjax[key]
  }

  onReject(uid, reason, callback) {
    if (Object.hasOwn(this.responsesAjax, uid)) return

    const controller = new AbortController()
    this.responsesAjax[uid] ||= {}
    this.responsesAjax[uid].status = 'rejecting'
    this.responsesAjax[uid].callback = callback
    this.responsesAjax[uid].controller = controller
    // Snapshot what the optimistic write below destroys: a failed reject must put the response
    // back as it was (`excessive` keeps its over-quota state), not flatten it to `completed`.
    this.responsesAjax[uid].priorStatus = this.data.cs.find(item => uid === item.response.uid)?.status

    rejectResponse({ surveyId: this.deps.getSurveyId(), uid, message: reason, signal: controller.signal })
      .then(response => this.onRejectCallback(uid, response))
      .catch(error => {
        if (!isAbort(error)) this.onRejectCallback(uid, { status: 'FAILED' })
      })

    this.data.cs.forEach(item => {
      if (uid === item.response.uid) item.status = 'dc-rejecting'
    })

    this.invalidateCalcCache()
    this.setData()
    this.render()
  }

  onRejectCallback(uid, data) {
    if (this.deps.getMode() === 'data-quality') {
      // Orphan callback: see onRestoreCallback — the empty skeleton has no `cs`, so the
      // branch below would throw. Treat the orphan as a no-op.
      if (!this.data.cs) {
        delete this.responsesAjax[uid]
        return
      }

      let error = null

      if (data.status === 'OK') {
        this.deps.onFetch(null, false)
        this.data.cs = this.data.cs.filter(item => uid !== item.response.uid)
      } else {
        // Fall back to the old hardcode only if the snapshot is somehow missing, so this can
        // never be worse than the previous behavior.
        const priorStatus = this.responsesAjax[uid]?.priorStatus || 'completed'
        this.data.cs.forEach(item => {
          if (uid === item.response.uid) item.status = priorStatus
        })
        error = { message: data.error || 'Oops. Something went wrong.' }
      }

      this.invalidateCalcCache()
      this.setData()
      this.render()

      const score = this.findContributingScore(u => uid === u)
      if (score) this.responsesAjax[uid]?.callback?.(error)
    } else {
      this.resetData()
    }

    delete this.responsesAjax[uid]
  }

  // Shared post-restore/reject score probe (the hoisted lookups + hasContributingVector
  // walk that both callbacks ran). Returns true if any matching uid still scores.
  findContributingScore(matches) {
    const quarantined = new Set(this.componentData.quarantineUIDs)
    const disabledVectors = new Set(this.disabledVectors)
    const filter = this.filter
    const filterMatch = u => {
      const isQuarantined = quarantined.has(u)
      return filter === 'all' || (!isQuarantined && filter !== 'quarantined') || (isQuarantined && filter !== 'flagged')
    }
    const vectorIds = this.componentData.vectorIds
    const hasContributingVector = s => {
      for (let i = 0; i < vectorIds.length; i++) {
        if (!disabledVectors.has(vectorIds[i]) && s[i + 1] > 0) return true
      }
      return false
    }

    return this.componentData.scores.some(
      s =>
        matches(s[0]) &&
        this.suspicionLevel <= this.componentData.suspicionScores[s[0]] &&
        hasContributingVector(s) &&
        filterMatch(s[0])
    )
  }

  // ---- Data shaping -------------------------------------------------------

  // Drop the calcData memo after an in-place this.data.cs mutation. The cache keys on the
  // this.data reference (see setData), which an in-place status write leaves unchanged —
  // so without this the restore/reject optimistic status update is served stale.
  invalidateCalcCache() {
    this._calcDataCache = null
  }

  setData(initial = false) {
    if (['idle', 'completed', 'disabled'].includes(this.jobData.status)) {
      const seedEmptyState = () => {
        this.jobData.vectors = {}
        getVisibleRaw().forEach(id => {
          this.jobData.vectors[id] = 'idle'
          this.componentData.vectorIds.push(id)
          this.componentData.vectorNames.push(VECTORS_NAMES[id])
        })
      }

      // Memoise on (data, status): @data is reassigned (never mutated) on fetch success.
      if (this._calcDataCache?.data === this.data && this._calcDataCache?.status === this.jobData.status) {
        this.componentData = this._calcDataCache.result
      } else {
        this.componentData = calcData(this.data, [], this.deps.getFeatures(), this.deps.getSurveyConfig())
        this._calcDataCache = {
          data: this.data,
          status: this.jobData.status,
          result: structuredClone(this.componentData)
        }
      }

      // An Advisory-only run still produces vector ids and group rollups, so it needs no special
      // seeding — it takes the same path as any other run that found something.
      if (this.componentData.scores.length > 0 || this.componentData.advisoryUIDs.length > 0) {
        if (initial && this.vectorSelection) {
          // The reader owns the checklist for the rest of the session, so a commit restores what
          // they chose instead of re-seeding. The parent rows are re-derived against the run that
          // just landed, since it decides which children are live under each parent.
          this.disabledVectors = [...this.vectorSelection]
          this.reconcileGroupParents()
        } else if (initial) {
          const knownIds = new Set()
          getVisibleRaw().forEach(id => {
            if (!this.componentData.vectorIds.includes(id) && !isAutoEnabledLikeVector(this.componentData, id)) return
            knownIds.add(id)
            if (!Object.hasOwn(this.componentData.groups, id)) return
            Object.entries(this.componentData.groups[id]).forEach(([childId, childItem]) => {
              if (!childItem.total && !isAutoEnabledLikeSubVector(this.componentData, childId, childItem)) return
              knownIds.add(childId)
            })
          })

          this.disabledVectors = getDefaultDisabledVectors().filter(item => knownIds.has(item))

          const disabledLookup = new Set(this.disabledVectors)

          VECTORS_GROUPS.forEach(group => {
            if (!Object.hasOwn(this.componentData.groups, group.id)) return
            const allDisabled = Object.keys(this.componentData.groups[group.id]).every(childId =>
              disabledLookup.has(childId)
            )
            if (allDisabled && !disabledLookup.has(group.id)) {
              this.disabledVectors.push(group.id)
              disabledLookup.add(group.id)
            }
          })
        }

        if (this.disabledVectors.length > 0) {
          this.componentData = calcData(
            this.data,
            this.disabledVectors,
            this.deps.getFeatures(),
            this.deps.getSurveyConfig()
          )
        }
      } else {
        seedEmptyState()
      }
    } else {
      this.componentData = emptyComponentData()

      getVisibleRaw().forEach(id => {
        this.componentData.vectorIds.push(id)
        this.componentData.vectorNames.push(VECTORS_NAMES[id])
      })
    }

    // Always seed group skeletons so the hierarchy renders on every status.
    seedEmptyGroups(this.componentData.groups)

    // Legacy DOM side effect: stamp the offending-responses count (an element outside
    // the React tree). Vanilla DOM — no jQuery.
    const count = this.componentData.scores.length || 0
    document.querySelectorAll('.offending-responses-amount').forEach(el => {
      el.textContent = count
    })
  }

  resetData() {
    // A live run outlives the mode switch at a slowed cadence; the per-vector map is what says
    // one is live (seeded by the Run gesture, refreshed per poll, dropped when the job completes).
    if (this.jobData.type === 'run' && Object.hasOwn(this.jobData, 'vectors')) {
      this.delay = 7 * DEFAULT_DELAY
    } else {
      Object.values(this.responsesAjax).forEach(responseAjax => {
        delete responseAjax.callback
      })

      this.includeVectors = null

      this.clearTimers()
      this.abortAjax()

      if (0 < this.componentData?.scores.length) {
        this.cachedData = structuredClone(this.data)
      } else {
        this.key = null
        this.cachedData = null
        this.jobData = { status: 'disabled' }
      }

      this.data = emptyComponentData()
      this.componentData = null
      this._calcDataCache = null

      this.setData()
      this.renderPanel()
    }

    this.destroyViews()
  }

  // Mode flipped away from data-centrifuge mid-poll: disable the job and tear down.
  disableJob() {
    this.jobData = { status: 'disabled' }
    this.resetData()
  }

  checkData() {
    if (this.deps.getMode() !== 'data-quality') {
      this.resetData()
    } else {
      if (this.jobData.type === 'run') {
        this.delay = DEFAULT_DELAY

        this.clearTimers()
        this.abortAjax()
        this.fetchJob()
      } else {
        this.delayFetchData()
      }
    }
  }

  // Per-vector status from /raw logs — shared by fetchData & fetchJob RUNNING branches.
  deriveVectorStatuses(data) {
    const groupChildren = {}
    VECTORS_GROUPS.forEach(g => {
      groupChildren[g.id] = g.vectors
    })

    const vectors = {}
    getVisibleRaw().forEach(id => {
      const parentLog = data.logs?.[id]
      if (parentLog !== undefined) {
        vectors[id] = parentLog < 100 ? 'running' : 'done'
      } else {
        const childIds = groupChildren[id]
        if (childIds && childIds.length > 0) {
          const childLogs = childIds.map(cid => data.logs?.[cid])
          if (childLogs.every(v => v === 100)) {
            vectors[id] = 'done'
          } else if (childLogs.some(v => v !== undefined && v > 0 && v <= 100)) {
            vectors[id] = 'running'
          } else {
            vectors[id] = this.jobData.vectors?.[id] === 'done' ? 'done' : 'waiting'
          }
        } else {
          vectors[id] = this.jobData.vectors?.[id] === 'done' ? 'done' : 'waiting'
        }
      }
    })
    return vectors
  }

  // ---- AJAX job-polling state machine -------------------------------------

  initFetchData() {
    this.clearTimers()
    this.abortAjax()

    this.disabledVectors = this.rememberedDisabledVectors()

    this.jobData.progress = { current: 0, real: 0 }

    if (this.jobData.status === 'fulfill') {
      this.jobData.type = null
    } else {
      this.jobData.type = 'fetch'
      this.jobData.status = 'waiting'
      this.jobData.vectors = vectorStatusMap('waiting')
    }
  }

  fetchCallback() {
    // Severity and filter are the reader's place, not the fetch's — deliberately not reset here.
    if (['fulfill', 'done', 'error'].includes(this.jobData.status)) {
      this.jobData.type = null
    }

    this.setData(true)

    if (this.deps.getMode() !== 'data-quality') return

    this.render()
  }

  fetchError() {
    this.delay = DEFAULT_DELAY
    this.abortAjax()
    this.ajaxTimer = null

    this.jobData.status = 'error'
    delete this.jobData.progress

    this.jobData.vectors = vectorStatusMap('error')

    this.fetchCallback()
  }

  async fetchData() {
    // A delayFetchApiKey callback can re-enter after teardown — don't restart.
    if (this.destroyed) return

    this.jobData.type = 'fetch'
    // A re-fetch that follows a completed run keeps 'fulfill' so its progress carries over.
    if (this.jobData.status !== 'fulfill') this.jobData.status = 'running'
    this.fetchCallback()

    const key = this.deps.getApiKey()
    this.abortController = new AbortController()

    let response
    try {
      response = await fetchDataQualityReport({
        url: this.deps.dqrUrl,
        apiKey: key?.code ?? 0,
        signal: this.abortController.signal
      })
    } catch (error) {
      if (isAbort(error)) return
      this.fetchError()
      return
    }
    this.abortController = null
    this.ajaxTimer = null

    switch (response.status) {
      case 'OK': {
        // A job token means the analysis is still being produced — poll it instead of mapping
        // a report whose numbers aren't final.
        if (response.job?.token) {
          this.jobData.token = response.job.token
          this.delayFetchJob()
        } else {
          this.report = mapDqrReport(response)
          this.reportLoadedAt = Date.now()
          // Drive the embedded DC engine from the /dqr aggregate; null when not yet fielded.
          this.data = this.report.dc?.data || emptyComponentData()

          this.key = key
          this.jobData = { status: 'idle' }

          this.fetchCallback()
        }
        break
      }
      case 'RUNNING':
        if (response.job?.token) {
          this.jobData.token = response.job.token
          this.delayFetchJob()
        } else {
          this.jobData.vectors = this.deriveVectorStatuses(response)

          // 'fulfill' always already has progress; only the non-fulfill path needs to seed it.
          if (this.jobData.status !== 'fulfill') this.jobData.progress ||= { current: 0, real: 0 }

          this.jobData.progress.current = Math.min(100, Math.max(this.jobData.progress.current, response.progress || 0))
          this.jobData.progress.real = Math.min(100, response.progress || 0)

          this.fetchCallback()
          this.ajaxTimer = setTimeout(() => this.fetchData(), this.delay)
        }
        break
      case 'INVALID KEY':
        this.deps.delayFetchApiKey({ forceUpdate: true, status: 'invalid', callback: () => this.fetchData() })
        break
      default:
        // FAIL / NO SCHEMA / anything unexpected.
        this.fetchError()
    }
  }

  // The last successful /dqr mapping, minus the internal DC-engine payload. Container reads this.
  getReport() {
    if (!this.report) return null
    const { tier, fielding, sections, surveyStatus } = this.report
    return { tier, fielding, sections, surveyStatus }
  }

  // A /dqr fetch failed. Paired with getReport() the container can tell a terminal failure
  // (no report) apart from a fetch still in flight, which both otherwise present as "no report",
  // and both apart from a refresh that failed over figures already on screen (error + report).
  hasError() {
    return this.jobData.status === 'error'
  }

  // When the figures on screen were last retrieved. Null until the first success, so a report and
  // its recency always arrive together.
  getReportLoadedAt() {
    return this.reportLoadedAt
  }

  // Author-driven retry from a failed section header. initFetchData() clears the error status,
  // so the sections fall back to the loading state while the refetch runs.
  retryFetch() {
    this.delayFetchData()
  }

  delayFetchData() {
    this.delay = DEFAULT_DELAY

    if (this.deps.isApiKeyInvalid()) {
      this.initFetchData()

      this.key = null

      this.data = emptyComponentData()
      this.cachedData = null

      this.deps.fetchApiKey({ forceUpdate: true, callback: () => this.fetchData() })
    } else {
      const key = this.deps.getApiKey()

      if (this.key === key && this.cachedData) {
        this.data = structuredClone(this.cachedData)
        this.cachedData = null

        delete this.jobData.progress
        delete this.jobData.vectors
      } else {
        this.initFetchData()

        this.data = emptyComponentData()
        this.cachedData = null

        this.fetchCallback()

        this.ajaxTimer = setTimeout(() => this.fetchData(), this.delay)
      }
    }

    this.fetchCallback()
  }

  async fetchJob() {
    // A delayFetchApiKey callback can re-enter after teardown — don't restart polling.
    if (this.destroyed) return

    this.jobData.type = 'run'
    this.jobData.status = 'running'

    this.fetchCallback()

    const payload = { survey_id: this.deps.getSurveyId(), command: 'analyzeRaw.dataCleaning' }
    if (this.jobData.token) payload.token = this.jobData.token
    if (this.includeVectors) payload.include_vectors = this.includeVectors

    this.abortController = new AbortController()
    let data
    try {
      data = await fetchDataCentrifugeJob({ data: payload, signal: this.abortController.signal })
    } catch (error) {
      if (isAbort(error)) return

      this.jobData.progress.current += 0.7
      this.jobData.progress.real += 0.4 / getVisibleRaw().length

      delete this.jobData.token

      this.abortController = null
      this.ajaxTimer = setTimeout(
        () => this.fetchJob(),
        (this.deps.getMode() === 'data-quality' ? 2 : 7) * DEFAULT_DELAY
      )
      return
    }
    this.abortController = null
    this.ajaxTimer = null

    switch (data.status) {
      case 'OK':
        if (this.deps.getMode() === 'data-quality') {
          this.jobData.status = 'fulfill'

          this.deps.onFetch(null, false)
          this.fetchCallback()

          this.ajaxTimer = setTimeout(() => this.delayFetchData(), FULFILL_QUARK)
        } else {
          this.disableJob()
        }
        break
      case 'RUNNING':
        if (data.job === 'failed') {
          if (this.deps.getMode() === 'data-quality') {
            this.deps.onFetch(null, false)
            this.delayFetchData()
          } else {
            this.disableJob()
          }
        } else {
          this.jobData.vectors = this.deriveVectorStatuses(data)

          this.jobData.progress.current = Math.min(100, Math.max(this.jobData.progress.current, data.progress || 0))
          this.jobData.progress.real = Math.min(100, data.progress || 0)

          this.jobData.token = data.token

          this.fetchCallback()
          this.ajaxTimer = setTimeout(() => this.fetchJob(), this.delay)
        }
        break
      case 'INVALID KEY':
        this.deps.delayFetchApiKey({ forceUpdate: true, status: 'invalid', callback: () => this.fetchJob() })
        break
      default:
        if (this.deps.getMode() === 'data-quality') {
          this.delayFetchData()
        } else {
          this.disableJob()
        }
    }
  }

  delayFetchJob(includeVectors = null) {
    this.includeVectors = includeVectors
    this.delay = DEFAULT_DELAY

    this.clearTimers()
    this.abortAjax()

    this.key = null
    this.disabledVectors = this.rememberedDisabledVectors()

    this.jobData.type = 'run'
    this.jobData.status = 'running'
    this.jobData.progress = { current: 0, real: 0 }
    this.jobData.vectors = vectorStatusMap('waiting')

    this.data = emptyComponentData()
    this.cachedData = null

    this.fetchCallback()

    this.ajaxTimer = setTimeout(() => this.fetchJob(), this.delay)
  }

  // ---- Listeners & teardown ----------------------------------------------

  // The shim owns the Backbone page.model subscriptions and forwards change:mode to
  // checkData().

  clearTimers() {
    if (this.ajaxTimer != null) clearTimeout(this.ajaxTimer)
    this.ajaxTimer = null
  }

  abortAjax() {
    this.abortController?.abort()
    this.abortController = null
  }

  destroy() {
    this.destroyed = true
    this.clearTimers()
    this.abortAjax()
    Object.values(this.responsesAjax).forEach(r => r.controller?.abort())
    this.responsesAjax = {}
    // page.model listeners are owned + torn down by the shim (onDestroy).
    this.subscribers.clear()
  }
}

// onRangeCheck/onSelect operate on the static raw list (lib.raw, NOT getVisibleRaw)
// filtered to vectors that either produced scores or ran clean (auto-enabled-like).
// Hoisted here because both handlers build the identical list.
function liveVectorIds(componentData) {
  return VECTORS_RAW.filter(id => componentData.vectorIds.includes(id) || isAutoEnabledLikeVector(componentData, id))
}
